import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import Button from "./button.js";
import Modal from "./modal.js";

const SingleDatePicker = ({ 
    value, 
    onChange, 
    name,
    label, 
    disabled = false,
    error,
    placeholder = "Select date",
    isRequired = false,
    showRequiredIndicator = true, // New prop to control asterisk visibility
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
    const { language } = useLanguage();
    const calendarRef = useRef(null);

    // Get locale for calendar based on language
    const getCalendarLocale = () => {
        const localeMap = {
            'am': 'hy-AM',
            'ru': 'ru-RU',
            'en': 'en-US'
        };
        return localeMap[language] || 'en-US';
    };

    // Format month and year with translation
    const formatMonthYear = (date) => {
        const monthNames = {
            'am': ['Հունվար', 'Փետրվար', 'Մարտ', 'Ապրիլ', 'Մայիս', 'Հունիս', 'Հուլիս', 'Օգոստոս', 'Սեպտեմբեր', 'Հոկտեմբեր', 'Նոյեմբեր', 'Դեկտեմբեր'],
            'ru': ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
            'en': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        };
        const month = monthNames[language]?.[date.getMonth()] || date.toLocaleString('default', { month: 'long' });
        return `${month} ${date.getFullYear()}`;
    };

    // Format weekday abbreviation
    const formatShortWeekday = (locale, date) => {
        const weekdayNames = {
            'am': ['Երկ', 'Երք', 'Չոր', 'Հնգ', 'Ուրբ', 'Շաբ', 'Կիր'],
            'ru': ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
            'en': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        };
        const dayIndex = date.getDay();
        return weekdayNames[language]?.[dayIndex] || date.toLocaleDateString(locale, { weekday: 'short' });
    };

    useEffect(() => {
        if (value) {
            setSelectedDate(new Date(value));
        } else {
            setSelectedDate(null);
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                // Don't close on outside click - let user use the buttons
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleConfirm = () => {
        if (selectedDate) {
            const formattedDate = selectedDate.toLocaleDateString('fr-CA'); // formats as YYYY-MM-DD
            onChange({
                target: {
                    name: name,
                    value: formattedDate
                }
            });
            setIsOpen(false);
        }
    };

    const handleClear = () => {
        setSelectedDate(null);
        onChange({
            target: {
                name: name,
                value: ''
            }
        });
        setIsOpen(false);
    };

    const formatDisplayDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); // DD.MM.YYYY
    };

    return (
        <div className="mb-4">
            {label && (
                <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-2">
                    {label} {isRequired && showRequiredIndicator && <span className="text-red-500 dark:text-red-400">*</span>}
                </label>
            )}
            <div className="relative">
                <input
                    type="text"
                    value={formatDisplayDate(value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly
                    onClick={() => !disabled && setIsOpen(true)}
                    className={`px-4 py-2 w-full bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-[14px] placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 cursor-pointer ${
                        error ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 dark:text-[#6e7681]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
            </div>
            {error && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-1 font-medium">{error}</p>
            )}

            <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                size="lg"
                className="p-0"
            >
                <div ref={calendarRef} className="p-4">
                    <h3 className="text-sm font-medium mb-3 text-gray-700 dark:text-[#b1bac4]">{label}</h3>
                    <div className="flex justify-center mb-4">
                        <Calendar
                            onChange={handleDateChange}
                            value={selectedDate}
                            className="react-calendar-custom"
                            locale={getCalendarLocale()}
                            formatShortWeekday={(locale, date) => formatShortWeekday(locale, date)}
                            formatMonthYear={(locale, date) => formatMonthYear(date)}
                        />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleClear}
                        >
                            {getTranslation('common.clear', language) || 'Clear'}
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleConfirm}
                            disabled={!selectedDate}
                        >
                            {getTranslation('common.confirm', language) || 'Confirm'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SingleDatePicker;

